import {
  Account,
  memoizedGetAccountTrips,
  memoizedGetProfile,
} from '@tuic/api-kit';
import {
  getActiveTripCodeOrFirst,
  getActiveTripOrFirst,
  getCurrentPhaseOrDefault,
  getEarliestStartDate,
  getLatestEndDate,
} from '@lib/trip';
import { getTariff, isVip1, isVip2 } from '@lib/booking/tariffs';
import { cache } from 'react';
import {
  FilterEnvironment,
  FilterEnvironmentClosure,
} from '../app/[locale]/[bookingCode]/_helpers/filter';
import { DateTime } from 'luxon';
import { PhasedBooking } from '@lib/phased-booking';

/**
 * Returns the trip codes from the given booking.
 */
export const getTripCodesFromBooking = (
  booking: Account.MyBooking2WithTravelPlanInfo,
): string[] => {
  return (booking.trips ?? [])
    .map(trip => trip.tripCode)
    .filter((trip): trip is string => typeof trip === 'string');
};

// export const getProfileTrip = (
//   booking: PhasedBooking,
// ): Account.MyTrip2 | undefined => {
//   let trip = getActiveTripOrFirst(booking);
//
//   return booking.original.trips.find(profileTrip => {
//     return profileTrip.tripCode === trip.code;
//   });
// };

/**
 * Creates a memoizable filter environment closure from the given booking.
 * Use this only once in the top most level of the app -- like a page.
 * Pass the closure to the components that need it to take usage of the memoization.
 */
export const createFilterEnvironmentClosureFromBooking = (
  booking: PhasedBooking,
): FilterEnvironmentClosure => {
  return cache(async (): Promise<FilterEnvironment> => {
    return await createFilterEnvironmentFromBooking(booking);
  });
};

/**
 * Creates a filter environment from the given booking.
 */
export const createFilterEnvironmentFromBooking = async (
  booking: PhasedBooking,
): Promise<FilterEnvironment> => {
  let activeTrip = getActiveTripOrFirst(booking);

  const token = booking.token;
  const profile =
    token.type === 'account'
      ? await memoizedGetProfile(token.idToken)
      : undefined;

  const accountTrips = await memoizedGetAccountTrips(token.idToken);

  const numberOfBookings = accountTrips?.length ?? 0;

  return {
    email: profile?.email,
    bookingCode: booking.original.bookingCode,
    tripCodes: booking.trips.map(trip => trip.code),
    shipCode: activeTrip?.ship?.code,
    spaBalconyCabin: booking.original.isSpaCabin,
    userHasAccount: booking.accessedViaUserAccount,
    tariffs: getTariffsFromBooking(booking),
    tripDateFrom: getEarliestStartDate(booking)?.toISO() ?? undefined,
    tripDateTo: getLatestEndDate(booking)?.toISO() ?? undefined,
    phase: getCurrentPhaseOrDefault(booking),
    numberOfBookings,
    isWebView: !!booking.isWebview,
  };
};

/**
 * Returns the tariffs from the given booking.
 */
const getTariffsFromBooking = (booking: PhasedBooking): string[] => {
  let tariffs = booking.original.trips.reduce<string[]>((collection, trip) => {
    let cabinTariffs = trip.cabins.reduce<string[]>(
      (cabinCollection, cabin) => {
        return [...cabinCollection, cabin.tariff ?? ''];
      },
      [],
    );

    return [...collection, ...cabinTariffs];
  }, []);

  if (booking.phaser?.tariff) {
    tariffs = [booking.phaser.tariff];
  }

  if (isVip1(booking)) {
    tariffs.push('VIP1');
  }
  if (isVip2(booking)) {
    tariffs.push('VIP2');
  }

  return [...new Set(tariffs)];
};

/**
 * Returns the traveller name for given travellerId from given booking.
 */
export const getTravellerName = (
  booking: PhasedBooking | Account.MyBooking2,
  travellerId: string | undefined,
): { firstName: string | undefined; lastName: string | undefined } => {
  if (typeof travellerId === 'undefined') {
    return {
      firstName: undefined,
      lastName: undefined,
    };
  }

  const original = 'original' in booking ? booking.original : booking;
  const traveller = original.travellers.find(
    traveller => traveller.id === travellerId,
  );

  return {
    firstName: traveller?.firstName,
    lastName: traveller?.lastName,
  };
};

/**
 * Returns the traveller name for the default traveller from given booking.
 */
export const getTravellerNameFromBooking = (
  booking: PhasedBooking | Account.MyBooking2,
): { firstName: string | undefined; lastName: string | undefined } => {
  const travellerId =
    'original' in booking ? booking.original.travellerId : booking.travellerId;
  return getTravellerName(booking, travellerId);
};

/**
 * Returns the traveller cabin for given travellerId from given booking.
 */
export const getTravellerCabin = (
  booking: PhasedBooking | Account.MyBooking2,
  travellerId: string | undefined,
): Account.Cabin | undefined => {
  if (typeof travellerId === 'undefined') {
    return undefined;
  }

  const original = 'original' in booking ? booking.original : booking;

  // Get the active trip code or the first one if no active trip is found
  const tripCode = getActiveTripCodeOrFirst(booking);

  if (typeof tripCode === 'undefined') {
    return undefined;
  }

  // Find the trip with given tripCode in the booking's trips.
  const trip = original.trips.find(trip => trip.tripCode === tripCode);

  // No trip -- no cabin.
  if (!trip) {
    return undefined;
  }

  // Find the cabin where the traveller is booked in.
  return trip.cabins.find(cabin => cabin.travellerIds.includes(travellerId));
};

/**
 * Returns the cabin for the default traveller from given booking.
 */
export const getTravellerCabinFromBooking = (
  booking: PhasedBooking | Account.MyBooking2,
): Account.Cabin | undefined => {
  const travellerId =
    'original' in booking ? booking.original.travellerId : booking.travellerId;

  const cabin = getTravellerCabin(booking, travellerId);

  if (typeof cabin === 'undefined') {
    return cabin;
  }

  if (
    'phaser' in booking &&
    typeof booking.phaser?.cabinNumber !== 'undefined'
  ) {
    return {
      ...cabin,
      cabinNumber: booking.phaser.cabinNumber,
    };
  }

  return cabin;
};

/**
 * Returns the MPI for the current traveller from given booking.
 */
export const getCurrentTravellerMPI = (
  booking: PhasedBooking | Account.MyBooking2,
): number | undefined => {
  let original = 'original' in booking ? booking.original : booking;

  if ('phaser' in booking && typeof booking.phaser?.mpi !== 'undefined') {
    return booking.phaser.mpi;
  }

  return getTravellerMPI(original.travellers, original.travellerId);
};

/**
 * Returns the MPI for the given travellerId from given booking.
 */
export const getTravellerMPI = (
  booking: PhasedBooking | Account.Traveller[],
  travellerId: string | undefined,
): number | undefined => {
  if (typeof travellerId === 'undefined') {
    return undefined;
  }

  let travellers = Array.isArray(booking)
    ? booking
    : booking.original.travellers;

  let traveller = travellers.find(traveller => {
    return traveller.id === travellerId;
  });

  if (!traveller || !('mpi' in traveller)) {
    return undefined;
  }

  if (typeof traveller.mpi === 'number') {
    return traveller.mpi;
  }

  if (typeof traveller.mpi === 'string') {
    return parseInt(traveller.mpi, 10);
  }

  return undefined;
};

/**
 * Use this function, everytime you need to get the current passengers datetime.
 * @param booking
 */
export const getNowBasedOnBooking = (
  booking: PhasedBooking,
): DateTime<true> => {
  let { phaser } = booking;

  if (typeof phaser?.datetime !== 'undefined') {
    let dateTime = DateTime.fromISO(phaser.datetime);

    if (dateTime.isValid) {
      const [firstTrip] = booking.trips;

      // If the trip has a valid startDateTime with zone info, use that zone.
      if (firstTrip?.startDateTime) {
        const zonedDateTime = dateTime.setZone(
          DateTime.fromISO(firstTrip.startDateTime, {
            setZone: true,
          }).zone.name,
        );

        if (zonedDateTime.isValid) {
          return zonedDateTime;
        }
      }

      return dateTime;
    }
  }

  return DateTime.now();
};

/**
 * Returns one of the `attrs` depending on the traveller, his or her VIP status or his or her tariff.
 */
export const getVipOrRegularAttribute = (
  booking: PhasedBooking | Account.MyBooking2WithTravelPlanInfo,
  attrs: {
    regular?: number | undefined;
    pro?: number | undefined;
    plus?: number | undefined;
    pur?: number | undefined;
    vip1?: number | undefined;
    vip2?: number | undefined;
  },
): number => {
  const regular = attrs.regular ?? -1;

  if (isVip1(booking)) {
    return Math.max(attrs.vip1 ?? -1, regular);
  }

  if (isVip2(booking)) {
    return Math.max(attrs.vip2 ?? -1, regular);
  }

  const tariff = getTariff(booking);

  if (tariff === 'PRO') {
    return Math.max(attrs.pro ?? -1, regular);
  }

  if (tariff === 'PUR') {
    return Math.max(attrs.pur ?? -1, regular);
  }

  if (tariff === 'PLUS') {
    return Math.max(attrs.plus ?? -1, regular);
  }

  return regular;
};

/**
 * Returns the MXP365 token from the given booking.
 */
export const getMxp365Token = (
  booking: PhasedBooking | Account.MyBooking2,
): string | undefined => {
  const original = 'original' in booking ? booking.original : booking;

  return original.mxp365;
};

/**
 * Returns the booking code from the given booking.
 */
export const getBookingCode = (
  booking: PhasedBooking | Account.MyBooking2,
): string => {
  const original = 'original' in booking ? booking.original : booking;

  return original.bookingCode;
};

/**
 * Returns the ship code of currently active trip.
 */
export const getActiveShipCode = (
  booking: PhasedBooking | Account.MyBooking2WithTravelPlanInfo,
): string | undefined => {
  if ('phaser' in booking && booking.phaser?.shipCode) {
    return booking.phaser.shipCode;
  }

  return getActiveTripCodeOrFirst(booking)?.substring(0, 3);
};
